import { format } from 'date-fns';
import { toast } from 'react-toastify';

import {
  getBranchLessons,
  getCredits,
  getNextLessons,
  postLessonAbsence,
  postLessonSpot,
} from '../api/bernabeu';

import { isMarketplaceHostname } from 'app/utils/homePath';

type LessonFilters = {
  fieldId?: string[],
  onlyMine?: boolean,
}

const errorMessage = (error: any, message: string) => error?.response?.data?.message ?? message;

// Lessons of the next 7 days of a branch (bernabeu branch id)
const fetchBranchLessons = (branchId: string, filters: LessonFilters = {}) => {
  let params: any = {};

  if (filters.fieldId?.length) {
    params.field_id = filters.fieldId;
  }

  if (filters.onlyMine) {
    params.only_mine = 1;
  }

  return getBranchLessons(branchId, params).then((response: any) => response.data);
}

// Next occurrences of a class schedule
const fetchNextLessons = (scheduleId: string) => {
  return getNextLessons(scheduleId).then((response: any) => response.data);
}

const fetchMarkLessonAbsence = (scheduleId: string, date: string) => {
  return postLessonAbsence(scheduleId, { date: date })
    .then((response: any) => response.data)
    .catch((error) => {
      const message = errorMessage(error, 'Erro ao marcar falta');

      toast.error(message);

      throw new Error(message);
    });
}

const fetchJoinLesson = (scheduleId: string, date: string, creditId: string) => {
  return postLessonSpot(scheduleId, { date: date, credit_id: creditId })
    .then((response: any) => response.data)
    .catch((error) => {
      const message = errorMessage(error, 'Erro ao entrar na aula');

      toast.error(message);

      throw new Error(message);
    });
}

// credits without field_ids can be used in every field of the makeup class service
const creditAcceptsField = (credit: any, fieldId: string) => {
  const fieldIds = credit.attributes.field_ids ?? [];

  return fieldIds.length === 0 || fieldIds.includes(fieldId);
}

// Active makeup class credits of the lesson branch with the lesson duration, some may be restricted to other fields
const fetchMakeupCredits = (lesson: any) => {
  return getCredits({
    status_id: 'active',
    origin_type: 'class',
    duration: lesson.attributes.duration,
    expired_at: format(new Date(), 'yyyy-MM-dd'),
    sort: 'expired_at',
  }).then((response: any) => response.data.filter((credit: any) => {
    const origin = credit.relationships?.origin;

    return origin?.attributes?.makeup_class === true &&
      Number(credit.attributes.duration) === Number(lesson.attributes.duration) &&
      origin?.relationships?.fields?.some((field: any) => field.id === lesson.relationships.field.id);
  }));
}

const getLessonBranch = (lesson: any) => lesson?.relationships?.field?.relationships?.branch;

// Lesson page does not depend on the branch page, so the path is the same on marketplace and branch subdomain
// the date selects which occurrence is shown, it is only used by the frontend
const LESSON_DATE_PARAM = 'date';

const getLessonPath = (scheduleId: string, date?: string) => {
  return `/aulas/${scheduleId}` + (date ? `?${LESSON_DATE_PARAM}=${date}` : '');
}

// Lessons grid lives inside the branch page: /unidade/:slug/aulas on marketplace, /aulas on branch subdomain
const getLessonsPath = (branchSlug?: string) => {
  if (isMarketplaceHostname()) {
    return branchSlug ? `/unidade/${branchSlug}/aulas` : '/';
  }

  return '/aulas';
}

export {
  LESSON_DATE_PARAM,
  creditAcceptsField,
  fetchBranchLessons,
  fetchJoinLesson,
  fetchMakeupCredits,
  fetchMarkLessonAbsence,
  fetchNextLessons,
  getLessonBranch,
  getLessonPath,
  getLessonsPath,
}
